import React from 'react';
import { styled } from '@compiled/react';
import { ButtonGroup } from '@atlaskit/button';
import { Stack } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import BreadcrumbRenderer from '@atlassian/jira-breadcrumbs/src/breadcrumb-renderer/index.tsx';
import ProjectSettingsBreadcrumb from '@atlassian/jira-breadcrumbs/src/common-breadcrumbs/project-settings/index.tsx';
import ProjectBreadcrumb from '@atlassian/jira-breadcrumbs/src/common-breadcrumbs/project/index.tsx';
import PageHeader from '@atlassian/jira-common-components-page-header/src/index.tsx';
import {
	type ProjectType,
	SOFTWARE_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { DocumentTitle } from '@atlassian/jira-global-document-title/src/DocumentTitle.tsx';
import { PageTopBar } from '@atlassian/jira-horizontal-nav-page-topbar/src/ui/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import type { AnalyticsData } from '../../state/analytics/types.tsx';
import type { InitiateAction } from '../../state/ui/add/actions.tsx';
import { CompassEmptyState } from '../compass-empty-state/index.tsx';
import { CompassTableCrossflowTouchPointWithEC } from '../compass-table-crossflow/index.tsx';
import CompassComponentsTable from '../compass-table/index.tsx';
import { RequestAccessState } from '../compass-table/request-access-state/index.tsx';
import CreateComponentButton from '../create-component-button/index.tsx';
import EmptyPlaceholder from '../empty-placeholder/index.tsx';
import Error from '../error/index.tsx';
import Filter from '../filter/index.tsx';
import { WrappedGlobalComponentsToggleButton } from '../global-components-toggle-button/index.tsx';
import { LoadingState } from '../loading-state/loading-state.tsx';
import Paginator from '../paginator/index.tsx';
import ComponentsTable from '../table/index.tsx';
import { HeaderBottombarContent } from './header-bottombar-content/index.tsx';
import { messages } from './messages.tsx';
import type { Status } from './selectors.tsx';

type StatusWithLoading = Status | 'loading';

export type Props = {
	intl: IntlShape;
	onAddComponent: () => InitiateAction;
	editable: boolean;
	status: Status;
	analyticsData: AnalyticsData;
	baseUrl: string;
	projectKey: string;
	projectName: string;
	projectType: ProjectType;
	isAdminContext: boolean;
	isLoading: boolean;

	globalComponentsProperty?: {
		loading: boolean;
		enabled: boolean;
		hasCompassPermissions: boolean;
		setProperty: (newValue: boolean) => void;
	};
};

// To become props when getWillShowNav4 is cleaned up
export type PropsNew = {
	onAddComponent: () => InitiateAction;
	editable: boolean;
	status: Status;
	analyticsData: AnalyticsData;
	projectKey: string;
	projectType: ProjectType;
	isLoading: boolean;
	globalComponentsProperty?: {
		loading: boolean;
		enabled: boolean;
		hasCompassPermissions: boolean;
		setProperty: (newValue: boolean) => void;
	};
};

function AppViewOld(props: Props) {
	const {
		onAddComponent,
		status,
		editable,
		baseUrl,
		projectKey,
		projectName,
		projectType,
		intl: { formatMessage },
		isAdminContext,
		isLoading,
		globalComponentsProperty = {
			loading: false,
			enabled: false,
			hasCompassPermissions: false,
			// Replace with lodash/noop
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			setProperty: () => {},
		},
	} = props;

	const isProjectTypeJsw = projectType === SOFTWARE_PROJECT;
	const breadcrumbs = <BreadcrumbRenderer />;

	let content = null;
	let searchBar = null;

	let actions = isProjectTypeJsw ? (
		<WrappedGlobalComponentsToggleButton
			globalComponentsProperty={{
				loading: globalComponentsProperty.loading,
				enabled: globalComponentsProperty.enabled,
				setProperty: globalComponentsProperty.setProperty,
			}}
			isEditable={editable}
		/>
	) : null;

	let createComponentButton = null;

	const statusWithLoading: StatusWithLoading =
		isLoading || globalComponentsProperty.loading ? 'loading' : status;

	switch (statusWithLoading) {
		case 'loading': {
			content = <LoadingState />;
			break;
		}

		case 'empty': {
			if (globalComponentsProperty.enabled) {
				content = <CompassEmptyState projectKey={projectKey} />;
			} else {
				content = <EmptyPlaceholder projectType={projectType} />;
			}
			break;
		}

		case 'has-components': {
			if (editable) {
				createComponentButton = (
					<CreateComponentButton
						testId="project-directories.components.content.create-component-button"
						loading={globalComponentsProperty.loading}
						globalComponentsEnabled={globalComponentsProperty.enabled}
						oldOnAddComponent={onAddComponent}
						projectKey={projectKey}
					/>
				);
			}

			// check if a user has access to compass, if not show request access state
			if (globalComponentsProperty.enabled) {
				if (!globalComponentsProperty.hasCompassPermissions) {
					content = <RequestAccessState />;
				} else {
					// FF on - Compass components
					content = (
						<CompassComponentsTable
							testId="project-directories.components.content.compass-components-table"
							projectKey={projectKey}
						>
							<Paginator />
						</CompassComponentsTable>
					);
				}
			}
			// Default / FF off - old Jira components
			else {
				content = (
					<>
						<ComponentsTable />
						<Paginator />
						<CompassTableCrossflowTouchPointWithEC />
					</>
				);
				searchBar = <Filter />;
			}

			actions = (
				<ActionsWrapper>
					{isProjectTypeJsw && (
						<WrappedGlobalComponentsToggleButton
							globalComponentsProperty={{
								loading: globalComponentsProperty.loading,
								enabled: globalComponentsProperty.enabled,
								setProperty: globalComponentsProperty.setProperty,
							}}
							isEditable={editable}
						/>
					)}
					{createComponentButton}
				</ActionsWrapper>
			);

			break;
		}

		case 'error': {
			content = <Error />;
			break;
		}

		default: {
			break;
		}
	}

	const pageContent = (
		<div data-test-id="project-directories.components.content">
			<PageHeader
				truncateTitle
				breadcrumbs={breadcrumbs}
				actions={actions}
				bottomBar={
					isProjectTypeJsw ? (
						<HeaderBottombarContent
							isGlobalComponentsEnabled={globalComponentsProperty.enabled}
							searchBar={searchBar}
							formatMessage={formatMessage}
						/>
					) : (
						searchBar
					)
				}
			>
				{formatMessage(messages.caption)}
			</PageHeader>
			<DocumentTitle title={formatMessage(messages.caption)} />
			{content}
		</div>
	);

	return isAdminContext ? (
		<ProjectBreadcrumb baseUrl={baseUrl} projectKey={projectKey} projectName={projectName}>
			<ProjectSettingsBreadcrumb
				baseUrl={baseUrl}
				projectKey={projectKey}
				projectType={projectType}
			>
				{pageContent}
			</ProjectSettingsBreadcrumb>
		</ProjectBreadcrumb>
	) : (
		<ProjectBreadcrumb baseUrl={baseUrl} projectKey={projectKey} projectName={projectName}>
			{pageContent}
		</ProjectBreadcrumb>
	);
}

function AppViewNew(props: PropsNew) {
	const {
		onAddComponent,
		status,
		editable,
		projectKey,
		projectType,
		isLoading,
		globalComponentsProperty = {
			loading: false,
			enabled: false,
			hasCompassPermissions: false,
			// Replace with lodash/noop
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			setProperty: () => {},
		},
	} = props;

	const { formatMessage } = useIntl();
	const isProjectTypeJsw = projectType === SOFTWARE_PROJECT;
	let content = null;
	let searchBar = null;
	let createComponentButton = null;
	const toggleButton = isProjectTypeJsw ? (
		<WrappedGlobalComponentsToggleButton
			globalComponentsProperty={{
				loading: globalComponentsProperty.loading,
				enabled: globalComponentsProperty.enabled,
				setProperty: globalComponentsProperty.setProperty,
			}}
			isEditable={editable}
		/>
	) : null;
	let actions = <ButtonGroup>{toggleButton}</ButtonGroup>;

	const statusWithLoading: StatusWithLoading =
		isLoading || globalComponentsProperty.loading ? 'loading' : status;

	switch (statusWithLoading) {
		case 'loading': {
			content = <LoadingState />;
			break;
		}

		case 'empty': {
			if (globalComponentsProperty.enabled) {
				content = <CompassEmptyState projectKey={projectKey} />;
			} else {
				content = <EmptyPlaceholder projectType={projectType} />;
			}
			break;
		}

		case 'has-components': {
			if (editable) {
				createComponentButton = (
					<CreateComponentButton
						testId="project-directories.components.content.create-component-button"
						loading={globalComponentsProperty.loading}
						globalComponentsEnabled={globalComponentsProperty.enabled}
						oldOnAddComponent={onAddComponent}
						projectKey={projectKey}
					/>
				);
			}

			// check if a user has access to compass, if not show request access state
			if (globalComponentsProperty.enabled) {
				if (!globalComponentsProperty.hasCompassPermissions) {
					content = <RequestAccessState />;
				} else {
					// FF on - Compass components
					content = (
						<CompassComponentsTable
							testId="project-directories.components.content.compass-components-table"
							projectKey={projectKey}
						>
							<Paginator />
						</CompassComponentsTable>
					);
				}
			}
			// Default / FF off - old Jira components
			else {
				content = (
					<>
						<ComponentsTable />
						<Paginator />
						<CompassTableCrossflowTouchPointWithEC />
					</>
				);
				searchBar = <Filter ariaLabel={formatMessage(messages.filterInputLabel)} />;
			}

			actions = (
				<ButtonGroup>
					{toggleButton}
					{createComponentButton}
				</ButtonGroup>
			);

			break;
		}

		case 'error':
			content = <Error />;
			break;

		default: {
			break;
		}
	}

	return (
		<div data-testId="project-directories.components.content">
			<PageTopBar
				actions={actions}
				bottomBar={
					isProjectTypeJsw ? (
						<Stack grow="fill">
							<HeaderBottombarContent
								isGlobalComponentsEnabled={globalComponentsProperty.enabled}
								searchBar={searchBar}
								formatMessage={formatMessage}
							/>
						</Stack>
					) : (
						searchBar
					)
				}
			/>
			{content}
		</div>
	);
}

export default componentWithCondition(getWillShowNav4, AppViewNew, injectIntl(AppViewOld));

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionsWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'& > :not(:last-child)': {
		marginRight: `${token('space.100', '8px')}`,
	},
});
